import { useTranslation } from "react-i18next";
import { BedDouble } from "lucide-react";
import { StatusBadge, type StatusKind } from "@/components/vroom/status-badge";
import { cn } from "@/lib/utils";

export type RolledStatus = "to_check" | "rejected" | "checked" | "exported" | null;

const STATUS_MAP: Record<Exclude<RolledStatus, null>, StatusKind> = {
  to_check: "todo",
  rejected: "rej",
  checked: "ok",
  exported: "exp",
};

const WEEKDAY_SHORT: string[] = ["zo", "ma", "di", "wo", "do", "vr", "za"];

export interface DayRowProps {
  date: Date;
  hoursMinutes: number;
  expectedMinutes: number;
  hasLodging: boolean;
  breakMinutes: number;
  rolledStatus: RolledStatus;
  onClick: () => void;
}

export function DayRow({
  date,
  hoursMinutes,
  expectedMinutes,
  hasLodging,
  breakMinutes,
  rolledStatus,
  onClick,
}: DayRowProps) {
  const { t } = useTranslation();

  const fillPercent =
    expectedMinutes > 0 ? Math.min(100, Math.round((hoursMinutes / expectedMinutes) * 100)) : 0;

  const dayLabel = WEEKDAY_SHORT[date.getDay()] ?? "";
  const dayNum = date.getDate();
  const statusKind = rolledStatus !== null ? STATUS_MAP[rolledStatus] : null;

  const totalHours = Math.floor(hoursMinutes / 60);
  const totalMins = hoursMinutes % 60;
  const hoursLabel = `${totalHours}:${String(totalMins).padStart(2, "0")}`;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-3 rounded-4 border border-line bg-surface px-3 py-2.5",
        "hover:bg-surface-1 active:bg-surface-2",
        "text-left",
      )}
    >
      {/* Day label */}
      <div className="flex w-9 flex-col items-center">
        <span className="text-[10px] font-medium uppercase text-ink-500">{dayLabel}</span>
        <span className="text-base font-semibold leading-none text-ink-900">{dayNum}</span>
      </div>

      {/* Progress bar */}
      <div className="flex flex-1 flex-col gap-1">
        <div className="flex items-center justify-between gap-2">
          <span className="font-mono text-xs tabular-nums text-ink-700">{hoursLabel}</span>
          <span className="text-[10px] text-ink-400">{fillPercent}%</span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-2">
          <div
            className={cn(
              "h-full rounded-full transition-all",
              fillPercent >= 100 ? "bg-st-ok-bg" : "bg-p-400",
            )}
            style={{ width: `${fillPercent}%` }}
            role="progressbar"
            aria-valuenow={fillPercent}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
      </div>

      {/* Chips row */}
      <div className="flex items-center gap-1.5">
        {breakMinutes > 0 && (
          <span className="inline-flex items-center rounded-full bg-surface-1 px-1.5 py-0.5 text-[10px] text-ink-500 ring-1 ring-line">
            {t("day-row.break.label")} {breakMinutes}&apos;
          </span>
        )}
        {hasLodging && (
          <BedDouble className="size-4 text-ink-500" aria-label={t("day-row.lodgingIcon.alt")} />
        )}
        {statusKind !== null && <StatusBadge status={statusKind} />}
      </div>
    </button>
  );
}
